import { SidUserClient } from "@sid/sid_client";
import { BaseInfo, CommonAttrInfo, LinkageUserData, ManageInfo, MemberAgreementInfo, ShippingInfo, SiteInfo, UserBase, UserBetsuma } from "@/commons/interfaces";
import { InjectionKey, reactive } from "vue";
import { LinkageUserItem } from "@/modules/linkage/linkageUserItem";
import BetsumaProfile from "@/components/Linkage/Sites/BetsumaProfile.vue";
import BetsumaConfirm from "@/components/Linkage/Sites/BetsumaConfirm.vue";
import BetsumaLoginCustom from "@/components/Linkage/Sites/BetsumaLoginCustom.vue";

// 別冊マーガレット関連定数定義
/** 連携ID */
export const BETSUMA_LINKAGE_ID = 15;
/** 連携名称 */
export const BETSUMA_LINKAGE_NAME = "Betsuma";
/** 連携パス */
export const BETSUMA_PATH = "betsuma";
/** サイト固有情報APIパス */
export const BETSUMA_SITE_UNIQ_API_PATH = "/betsuma/me";
/** デフォルトコールバックURL */
export const BETSUMA_DEFAULT_CALLBACK_URL = process.env.VUE_APP_BETSUMA_DEFAULT_CALLBACK_URL;
/** 許容コールバックURL */
export const BETSUMA_ACCEPT_CALLBACK_URLS = process.env.VUE_APP_BETSUMA_ACCEPT_CALLBACK_URLS.split(",").map((url: string) => `^${url}(\\?.*)?$`);
/** サービス規約URL */
export const BETSUMA_SERVICE_TERM_URL = process.env.VUE_APP_BETSUMA_SERVICE_TERM_URL;
/** メールアドレスドメイン */
export const BETSUMA_EMAIL_DOMAINS = [
    "rimacomi.sid.shueisha.co.jp",
];
/** 基本属性情報入力 */
export const BETSUMA_PROFILE_COMPONENT = BetsumaProfile;
/** 基本属性情報確認 */
export const BETSUMA_CONFIRM_COMPONENT = BetsumaConfirm;
/** コールバックURLの任意指定 */
export const BETSUMA_IS_CALLBACK_OPTIONAL = true;

/**
 * 別冊マーガレット連携画面用基本属性情報管理クラス
 */
export class BetsumaUserBase implements LinkageUserData<UserBase> {

    /** インジェクションキー */
    public static injectionKey: InjectionKey<LinkageUserItem<LinkageUserData<UserBase>>> = Symbol("Shueisha_MangasaiUserBase");

    /** 基本属性情報 */
    public readonly data: UserBase = reactive(new UserBase());
    /**
     * @inheritDoc LinkageUserData
     */
    public initData(): void {
        this.copy(new UserBase());
    }

    /**
     * @inheritDoc LinkageUserData
     */
    public getData(): UserBase {
        return this.data;
    }

    /**
     * @inheritDoc LinkageUserData
     */
    public copy(obj: UserBase): void {
        this.data.identityId = obj.identityId;
        this.data.userName = obj.userName;
        this.data.baseInfo = Object.assign(new BaseInfo(), obj.baseInfo);
        this.data.shippingInfo = obj.shippingInfo?.map(inf => Object.assign(new ShippingInfo(), inf)) || [];
        this.data.siteInfo = obj.siteInfo?.map(inf => Object.assign(new SiteInfo(), inf)) || [];
        this.data.commonAttrInfo = Object.assign(new CommonAttrInfo(), obj.commonAttrInfo);
        this.data.memberAgreeInfo = Object.assign(new MemberAgreementInfo(), obj.memberAgreeInfo);
        this.data.manageInfo = Object.assign(new ManageInfo(), obj.manageInfo);
    }

    /**
     * @inheritDoc LinkageUserData
     */
    public async fetch(sidClient: SidUserClient): Promise<void> {
        const response = await sidClient.getUserAttr()
            .then(res => res.data)
            .catch(res => {
                if (res.response?.status === 404) {
                    return new UserBase();
                } else {
                    throw new Error("Failed to get information.");
                }
            });
        this.copy(response);
    }

    /**
     * @inheritDoc LinkageUserData
     */
    public checkRequired(): boolean {
        // 基本属性情報の有無
        if (!this.data.identityId) return false;
        // 登録者情報
        if (!this.data.baseInfo.email) return false;

        // 属性情報
        if (!this.data.commonAttrInfo.gender) return false;
        if (!this.data.commonAttrInfo.birthday) return false;

        return true;
    }

    /**
     * @inheritDoc LinkageUserData
     */
    getInjectKey(): InjectionKey<LinkageUserItem<LinkageUserData<UserBase>>> {
        return BetsumaUserBase.injectionKey;
    }

}

/**
 * 別冊マーガレット連携画面用サイト固有情報管理クラス
 */
export class BetsumaSiteUniq implements LinkageUserData<UserBetsuma> {

    /** インジェクションキー */
    public static injectionKey: InjectionKey<LinkageUserItem<LinkageUserData<UserBetsuma>>> = Symbol("UserBetsuma");

    /** 別冊マーガレットサイト固有情報 */
    public readonly data: UserBetsuma = reactive(new UserBetsuma());

    /**
     * @inheritDoc LinkageUserData
     */
    public initData(): void {
        this.copy(new UserBetsuma());
    }

    /**
     * @inheritDoc LinkageUserData
     */
    public getData(): UserBetsuma {
        return this.data;
    }

    /**
     * @inheritDoc LinkageUserData
     */
    public copy(obj: UserBetsuma): void {
        this.data.identityId = obj.identityId;
        this.data.userName = obj.userName;
        this.data.isSendMailMagazine = obj.isSendMailMagazine;
        this.data.manageInfo = Object.assign({}, obj.manageInfo);
    }

    /**
     * @inheritDoc LinkageUserData
     */
    public async fetch(sidClient: SidUserClient): Promise<void> {
        const response = await sidClient.requestApi({ method: "GET", path: BETSUMA_SITE_UNIQ_API_PATH })
            .then(res => res.data)
            .catch(res => {
                if (res.response?.status === 404) {
                    return new UserBetsuma();
                } else {
                    throw new Error("Failed to get information.");
                }
            });
        this.copy(response);
    }

    /**
     * @inheritDoc LinkageUserData
     */
    public checkRequired(): boolean {
        // サイト固有情報の有無
        if (this.data.isSendMailMagazine === null || this.data.isSendMailMagazine === undefined) return false;
        return true;
    }

    /**
     * @inheritDoc LinkageUserData
     */
    getInjectKey(): InjectionKey<LinkageUserItem<LinkageUserData<UserBetsuma>>> {
        return BetsumaSiteUniq.injectionKey;
    }
}